import { Injectable } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { Subject } from 'rxjs';
import { EnvironmentService } from './environment.service';
import { PGUtilities } from '../utilities/pg-utilities';

export type Websocket2MessageType = 'register'|'unregister'|'request'|'message';

export class Websocket2Message { 
    type:Websocket2MessageType;
    service:string;
    id?:string;
    data?:any;
    error?:any;
}

export type Websocket2RegistrationStatus = 'offline'|'connected'|'registered'|'unauthorized';

export class Websocket2Registration {
    service:string = null;
    auth:any = null;

    status:Websocket2RegistrationStatus = 'offline';
    profile:any = null;
    error:any = null;
    
    statusChange = new Subject<Websocket2RegistrationStatus>();
    onMessage = new Subject<any>();

    private _sentRequests:{ [id:string]: { 
        resolve:(data:any) => void, 
        reject:(error:any) => void 
    } } = {};

    private _listeners:{ [id:string]: (...args: any[]) => void } = {};
    private _disposed = false;

    constructor(private socket:Socket, service:string, auth?:any, private onDispose?:() => void) {
        this.service = service;
        this.auth = auth;

        this._listeners['connect'] = () => { this._handleConnect(); }
        this._listeners['disconnect'] = () => { this._handleDisconnect(); }
        this._listeners['message'] = (data) => { this._handleMessage(data); }

        this._addListeners();

        if(socket.ioSocket.connected) {
            this._handleConnect();
        }
    }

    private _addListeners() {
        for(let i in this._listeners) {
            this.socket.on(i, this._listeners[i]);
        }
    }

    private _removeListeners() {
        for(let i in this._listeners) {
            this.socket.removeListener(i, this._listeners[i]);
        }
    }

    private _handleMessage(message:Websocket2Message) {
        if(message.service == null || message.service == this.service) {
            console.log('WS2Reg <RCVD', this.service, message.type, message.data)

            if(message.type == 'register') {
                if(typeof message.error != 'undefined') {
                    this.status = 'unauthorized';
                    this.profile = null;
                    this.error = message.error;
                    this.statusChange.next(this.status);
                }
                else {
                    this.status = 'registered';
                    this.profile = message.data;
                    this.error = null;
                    this.statusChange.next(this.status);
                }
            }
            else if(message.type == 'request') {
                if(this._sentRequests[message.id] != null) {
                    let _cRequest = this._sentRequests[message.id];
                    delete this._sentRequests[message.id];
                    
                    if(typeof message.error != 'undefined') {
                        _cRequest.reject(message.error);
                    }
                    else {
                        _cRequest.resolve(message.data);
                    }
                }
            }
            else {
                this.onMessage.next(message.data);
            }
        }
    }

    private _handleConnect() {
        this.status = 'connected';
        this.profile = null;
        this.error = null;
        this.statusChange.next(this.status);

        if(typeof this.auth == 'function') {
            let _result = this.auth();

            if(_result instanceof Promise) {
                _result.then((data) => {
                    this._emit({ 
                        type: 'register',
                        data: data 
                    });
                })
            }
            else {
                this._emit({ 
                    type: 'register',
                    data: _result 
                });
            }
        }
        else {
            this._emit({ 
                type: 'register',
                data: this.auth 
            });
        }
    }

    private _handleDisconnect() {
        this.status = 'offline';
        this.profile = null;
        this.error = null;
        this.statusChange.next(this.status);

        for(let i in this._sentRequests) {
            let _cRequest = this._sentRequests[i];
            delete this._sentRequests[i];
            _cRequest.reject(null);
        }
    }

    send(data:any) {
        if(this.status == 'registered') {            
            this._emit({ 
                type: 'message',
                data: data 
            });
        }
    }

    request(data:any) {
        return new Promise<any>((resolve, reject) => {
            if(this.status != 'registered') {
                reject();
            }
            else {
                let _cId = PGUtilities.getRandomId();

                this._sentRequests[_cId] = {
                    resolve: resolve,
                    reject: reject
                }

                this._emit({ 
                    type: 'request',
                    id: _cId, 
                    data: data 
                });
            }
        })
    }

    unregister() {
        if(this._disposed) {
            return;
        }

        this._disposed = true;

        if(this.status == 'registered') {
            this._emit({ 
                type: 'unregister'
            });
        }

        this._removeListeners();
        this._handleDisconnect();
        this.socket.disconnect();

        if(this.onDispose != null) {
            this.onDispose();
        }
    }

    private _emit(data:any) {
        data.service = this.service
        
        this.socket.emit('message', data);
        console.log('WS2Reg SENT>', this.service,  data.type, data.data)
    }
}

@Injectable({
    providedIn: 'root'
})
export class Websocket2Service {

    version:{ [service:string]: string } = null;

    constructor(public environmentService:EnvironmentService) {
    }

    private _socketList:Array<Socket> = []

    register(service:string, auth?:any) {
        if(this.environmentService.environment.WebSocket != null) {
            let _socket = new Socket({ 
                url: this.environmentService.environment.WebSocket, 
                options: {} 
            })

            _socket.on('connect', () => { 
                console.log('WS2 connect')
            });
    
            _socket.on('disconnect', () => { 
                console.log('WS2 disconnect')
            });

            this._socketList.push(_socket)

            return new Websocket2Registration(_socket, service, auth, () => {
                let _idx = this._socketList.indexOf(_socket);
                if(_idx != -1) {
                    this._socketList.splice(_idx, 1);
                }
            });
        }
    }

    restart(token:string) {
        return new Promise<void>((resolve, reject) => {
            let _socket = new Socket({ 
                url: this.environmentService.environment.WebSocket, 
                options: {} 
            })

            _socket.once('connect', () => {
                _socket.once('admin', (data) => {
                    if(typeof data.error != 'undefined') {
                        _socket.disconnect();
                        reject()
                    }
                    else {
                        _socket.once('restart', () => {
                            for(let _socket of this._socketList) {
                                _socket.connect()
                            }
                            _socket.disconnect();
                            resolve()
                        })
        
                        _socket.emit('message', { service: 'admin', data: { action: 'restart' } })
                    }
                })
        
                _socket.emit('admin', token)
            })
        })
    }
}